/**
 * HUD боя по макетам docs/ux/voxel 01, 03, 05 — чистое представление {@link HudModel} + колбэки {@link HudActions}.
 *
 * Раскладка «одна рука»: всё, что жмут в бою (навыки, «Автонавыки»), — в нижней трети над вкладками (C5,
 * место — `--kit-tabbar-h`); сверху только показатели (монеты, волны/босс, штаб); по бокам — иконки панелей.
 * Отступы от выреза и кнопок Telegram — `--app-inset-*` / `env(safe-area-inset-*)` (см. ./hud.css).
 */
import type { HeroId } from "@newfungame/contracts";
import { Fragment, type ComponentChildren } from "preact";
import { Bar, formatCompact, Icon, type IconName, Plate, Tile, Toggle } from "../kit";
import type { HudActions, HudGoal, HudModel, HudPanel, HudSideIcon, HudSkill } from "./model";
import type { HudKey, HudT } from "./messages";
import { useTweenedNumber } from "./useTween";
import { HintMark } from "../hints";
import "./hud.css";

export const SKILL_ICON: Record<HeroId, IconName> = {
  gunner: "skill_barrage",
  bomber: "skill_airstrike",
  sparky: "skill_chainLightning",
  blaze: "skill_firewall",
  frosty: "skill_freeze",
  radiation: "skill_firewall",
  cryo: "skill_freeze",
  laser: "skill_chainLightning",
  plague: "skill_chainLightning",
};

const PANEL_ICON: Record<HudPanel, IconName> = {
  tasks: "tasks",
  streak: "streak",
  season: "rank",
  chests: "coin",
  friends: "friends",
  rank: "rank",
};

function Coins({ coins, t }: { coins: number; t: HudT }) {
  const { value, bumps } = useTweenedNumber(coins);
  return (
    <Plate class="hud-coins" data-testid="hud-coins" data-value={coins} aria-label={`${t("hud.coins")}: ${coins}`}>
      {/* key меняется на каждый приход — анимация «прыжка» монеты перезапускается */}
      <span key={bumps} class={bumps > 0 ? "hud-coins__icon hud-coins__icon--bump" : "hud-coins__icon"}>
        <Icon name="coin" size={28} />
      </span>
      <span class="hud-coins__value">{formatCompact(value)}</span>
    </Plate>
  );
}

function WaveStrip({ wave, t }: { wave: HudModel["wave"]; t: HudT }) {
  return (
    <Plate class="hud-waves" data-testid="hud-waves" aria-label={t("hud.wave", { n: wave.number })}>
      <ol class="hud-waves__nodes">
        {wave.nodes.map((n, i) => (
          <Fragment key={n.wave}>
            {i > 0 && <li class="hud-waves__link" aria-hidden="true" />}
            <li
              class={`hud-waves__node${n.current ? " hud-waves__node--current" : ""}${n.boss ? " hud-waves__node--boss" : ""}`}
              data-wave={n.wave}
              data-boss={n.boss ? "1" : "0"}
              aria-current={n.current ? "step" : undefined}
            >
              {n.boss && !n.current ? <Icon name="skull" size={22} /> : n.wave}
            </li>
          </Fragment>
        ))}
      </ol>
      <Bar
        value={wave.progress}
        color="gold"
        class="hud-waves__progress"
        label={t("hud.waveProgress")}
        testId="hud-wave-progress"
      />
    </Plate>
  );
}

function GoalTag({ goal, t }: { goal: HudGoal; t: HudT }) {
  const hero = t(`hud.heroName.${goal.heroId}` as HudKey);
  return (
    <Plate class="hud-goal" data-testid="hud-goal" data-wave={goal.wave} data-hero={goal.heroId}>
      <Icon name={goal.heroId as IconName} size={22} />
      <span>{t(goal.boss ? "hud.goalBossHero" : "hud.goalHero", { n: goal.wave, hero })}</span>
    </Plate>
  );
}

function BossBar({ boss, wave, t }: { boss: NonNullable<HudModel["boss"]>; wave: number; t: HudT }) {
  const nameKey = `hud.bossName.${boss.bossId}` as HudKey;
  const name = t(nameKey);
  return (
    <Plate class="hud-boss" data-testid="hud-boss">
      <Icon name="skull" size={40} />
      <div class="hud-boss__body">
        <div class="hud-boss__title">
          <span>{t("hud.bossWave", { n: wave })}</span>
          {name !== nameKey && <span class="hud-boss__name">{name}</span>}
        </div>
        <Bar value={boss.permille} label={t("hud.boss")} testId="hud-boss-hp" />
      </div>
      <span class="hud-boss__pct">{Math.round(boss.permille / 10)}%</span>
    </Plate>
  );
}

function HqBar({ hq, t }: { hq: HudModel["hq"]; t: HudT }) {
  return (
    <Plate class="hud-hq" data-testid="hud-hq" data-look={hq.look} aria-label={`${t("hud.hq")}: ${hq.hp}/${hq.hpMax}`}>
      <Icon name="tower" size={30} />
      <span class="hud-hq__body">
        <Bar
          value={hq.permille}
          color={hq.look === "critical" || hq.look === "destroyed" ? "red" : "green"}
          label={t("hud.hq")}
          class="hud-hq__bar"
          testId="hud-hq-hp"
        />
        <span class="hud-hq__text" aria-hidden="true">
          {t("hud.hqHp", { hp: formatCompact(hq.hp), max: formatCompact(hq.hpMax) })}
        </span>
      </span>
    </Plate>
  );
}

function SkillButton({
  s,
  t,
  hinted,
  introDelay,
  onCast,
}: {
  s: HudSkill;
  t: HudT;
  hinted: boolean;
  introDelay?: number;
  onCast(heroId: HeroId): void;
}) {
  const name = t(`hud.skill.${s.heroId}` as HudKey);
  const label = s.ready ? t("hud.skillReady", { name }) : t("hud.skillCooldown", { name, s: s.secondsLeft });
  return (
    <button
      type="button"
      class={`hud-skill${hinted ? " hint-target" : ""}${introDelay === undefined ? "" : " hud-skill--intro"}`}
      data-testid={`hud-skill-${s.heroId}`}
      data-ready={s.ready ? "1" : "0"}
      data-active={s.active ? "1" : "0"}
      aria-label={label}
      aria-disabled={!s.ready}
      style={{ "--cd": String(s.cooldownPermille / 1000), "--intro-delay": `${introDelay ?? 0}s` }}
      onClick={() => {
        if (s.ready) onCast(s.heroId);
      }}
    >
      <span class="hud-skill__face">
        <Icon name={SKILL_ICON[s.heroId]} size={40} class="hud-skill__art" />
      </span>
      {!s.ready && (
        <span class="hud-skill__cd" aria-hidden="true">
          {s.secondsLeft}
        </span>
      )}
      {hinted && <HintMark label={t("hud.hintSkill")} testId="hint-skill" />}
    </button>
  );
}

function Side({
  icons,
  side,
  t,
  onOpen,
}: {
  icons: HudSideIcon[];
  side: "left" | "right";
  t: HudT;
  onOpen(p: HudPanel): void;
}) {
  if (icons.length === 0) return null;
  return (
    <nav class={`hud-side hud-side--${side}`} data-testid={`hud-side-${side}`}>
      {icons.map((i) => (
        <Tile
          key={i.panel}
          icon={PANEL_ICON[i.panel]}
          label={i.label ?? t(`hud.panel.${i.panel}` as HudKey)}
          value={i.label !== undefined}
          badge={i.badge}
          testId={`hud-open-${i.panel}`}
          onClick={() => onOpen(i.panel)}
        />
      ))}
    </nav>
  );
}

export interface HudViewProps {
  model: HudModel;
  actions: HudActions;
  t: HudT;
  /** Навык, на который показывает подсказка новичку; `null` — подсказки нет. */
  hintSkill?: HeroId | null;
  /** Перекличка героев: через сколько секунд вспыхнет кнопка навыка. */
  introPulses?: Partial<Record<HeroId, number>>;
  /** Слой поверх поля, под кнопками HUD: посылка. */
  children?: ComponentChildren;
}

export function HudView({ model, actions, t, hintSkill = null, introPulses, children }: HudViewProps) {
  const m = model;
  return (
    <div
      class="kit hud"
      data-testid="hud"
      data-wave={m.wave.number}
      data-phase={m.wave.phase}
      data-boss={m.boss ? "1" : "0"}
    >
      {children}
      <header class="hud-top">
        <Coins coins={m.coins} t={t} />
        {m.boss ? null : <WaveStrip wave={m.wave} t={t} />}
        {!m.boss && m.wave.goal && <GoalTag goal={m.wave.goal} t={t} />}
        <HqBar hq={m.hq} t={t} />
        {m.boss && <BossBar boss={m.boss} wave={m.wave.number} t={t} />}
      </header>
      <Side icons={m.side.left} side="left" t={t} onOpen={actions.onOpen} />
      <Side icons={m.side.right} side="right" t={t} onOpen={actions.onOpen} />
      <footer class="hud-bottom">
        <div class="hud-toggles">
          {!m.autoSkills.hidden && (
            <Toggle
              class="hud-auto"
              on={m.autoSkills.enabled}
              locked={m.autoSkills.locked}
              label={t("hud.autoSkills")}
              icon="gear"
              testId="hud-auto"
              onChange={(on) => (m.autoSkills.locked ? actions.onLocked("auto") : actions.onToggleAuto(on))}
            />
          )}
          {!m.speed.hidden && (
            <Toggle
              class="hud-auto"
              on={m.speed.fast}
              locked={m.speed.locked}
              label={t("hud.speed")}
              testId="hud-speed"
              onChange={(on) => (m.speed.locked ? actions.onLocked("speed") : actions.onToggleSpeed(on))}
            />
          )}
          {m.admin && (
            <div class="hud-admin">
              <button type="button" data-testid="hud-admin-speed" onClick={() => actions.onAdminSpeed()}>
                x{m.admin.speed}
              </button>
              <button
                type="button"
                data-testid="hud-admin-coins"
                aria-label={t("hud.coins")}
                onClick={() => actions.onAdminCoins()}
              >
                +<Icon name="coin" size={16} />
              </button>
            </div>
          )}
        </div>
        {m.skills.length > 0 && (
          <div class="hud-skills" data-count={m.skills.length}>
            {m.skills.map((s) => (
              <SkillButton
                key={s.heroId}
                s={s}
                t={t}
                hinted={s.heroId === hintSkill}
                introDelay={introPulses?.[s.heroId]}
                onCast={actions.onCastSkill}
              />
            ))}
          </div>
        )}
      </footer>
    </div>
  );
}
